import { must } from "@/common/utils/must";
import objectUtils from "@/common/utils/objectUtils";
import ExperienceData from "@/modules/ExperienceData";
import ExperienceMetadata from "@/modules/ExperienceMetadata";
import { normalizeExperienceBody } from "@/modules/experiencePayloadUtils";
import type {
  ChainRecommendation,
  ExperiencePayload,
} from "@/types/experience";
import type { ChainRequestBody } from "@/modules/experienceApiUrl";

/** How a failed experience or chain request rejects. */
export interface DataAccessError {
  status: number;
  errorString: string;
}

/** `data` is the parsed JSON body, or the raw text when the body is not JSON. */
interface HttpResponse {
  status: number;
  data: unknown;
}

// Same Accept and Content-Type values the API has always received.
const ACCEPT = "application/json, text/plain, */*";
const JSON_CONTENT_TYPE = "application/json;charset=UTF-8";

/**
 * Settles like axios 0.18: resolves on 2xx, rejects with `{ response }` on any
 * other status, and with Error("Network Error") when no response is read.
 */
function request(url: string, init: RequestInit): Promise<HttpResponse> {
  // A non-OK body is read too: getResponseError takes the API's message from it.
  return fetch(url, init).then(
    res =>
      res.ok
        ? readBody(res)
        : readBody(res).then(response => Promise.reject({ response })),
    () => Promise.reject(new Error("Network Error")),
  );
}

function readBody(res: Response): Promise<HttpResponse> {
  return res.text().then(
    text => {
      let data: unknown = text;
      try {
        data = JSON.parse(text);
      } catch {
        // not JSON: keep the text
      }
      return { status: res.status, data };
    },
    () => Promise.reject(new Error("Network Error")),
  );
}

function getResponseError(response: HttpResponse): DataAccessError {
  let errorString;
  const data = response.data as { message?: string } | null;
  if (data && data.message) {
    errorString = data.message;
  } else if (response.status === 404) {
    errorString = "Item not found.";
  } else {
    errorString = response.toString();
  }

  return {
    status: response.status,
    errorString: errorString,
  };
}

function checkExperienceBody(
  data: ExperiencePayload | null,
  httpStatus: number | null | undefined,
): DataAccessError | true {
  const status = httpStatus != null ? httpStatus : 200;
  if (objectUtils.isString(data) || objectUtils.isString(data && data.data)) {
    return {
      status: status,
      errorString: "Malformed JSON.",
    };
  }

  const video = must(data, "experience body").video;
  if (video === undefined || video === null) {
    return {
      status: status,
      errorString: "Experience is missing a video.",
    };
  }

  return true;
}

function checkResponse(response: HttpResponse): DataAccessError | true {
  return checkExperienceBody(
    response.data as ExperiencePayload | null,
    response.status,
  );
}

/**
 * Build ExperienceData from prefetched JSON (the same shape as a fetched body).
 */
export function createExperienceDataFromPayload(
  raw: unknown,
): Promise<ExperienceData> {
  return new Promise((resolve, reject) => {
    const body = normalizeExperienceBody(raw);
    const check = checkExperienceBody(body, 200);
    if (check !== true) {
      reject(check);
      return;
    }
    const experienceMetadata = new ExperienceMetadata(
      must(window.pixmoto, "window.pixmoto").experienceMetadata,
    );
    // Refresh the per-fetch metadata id from the prefetched body so analytics
    // attributes events to the experience actually playing after an in-place
    // chain swap. Fresh copy — window.pixmoto is untouched.
    if (body && body.idexperience != null) {
      experienceMetadata.idexperience = body.idexperience;
    }
    const experienceData = new ExperienceData(experienceMetadata, body);
    resolve(experienceData);
  });
}

export function getExperience(url: string): Promise<ExperienceData> {
  return new Promise((resolve, reject) => {
    request(url, { headers: { Accept: ACCEPT } })
      .then(response => {
        const check = checkResponse(response);
        if (check !== true) {
          reject(check);
        } else {
          const body =
            normalizeExperienceBody(response.data) ||
            (response.data as ExperiencePayload | null);
          const experienceMetadata = new ExperienceMetadata(
            must(window.pixmoto, "window.pixmoto").experienceMetadata,
          );
          // window.pixmoto.experienceMetadata is boot-frozen to the URL/embed
          // id and never updates on in-place chain swaps. Refresh the per-fetch
          // copy's idexperience from the fetched body so analytics (which reads
          // metadata.idexperience) attributes events to the experience actually
          // playing. The ExperienceMetadata copy is fresh (assign(this, ...)),
          // so window.pixmoto is untouched.
          if (body && body.idexperience != null) {
            experienceMetadata.idexperience = body.idexperience;
          }
          const experienceData = new ExperienceData(experienceMetadata, body);
          resolve(experienceData);
        }
      })
      .catch(error => {
        if (error.response) {
          reject(getResponseError(error.response));
        } else {
          reject({
            status: 0,
            errorString: error.toString(),
          });
        }
      });
  });
}

/**
 * POST /publisher/chain. Resolves the recommendation array (response
 * `data.chain`), rejects with a shaped {status, errorString} error (mirrors
 * getExperience). No weights/pt in the body — config is server-side, rate
 * limit keys on IP.
 */
export function getChain(
  url: string,
  body: ChainRequestBody,
): Promise<ChainRecommendation[]> {
  return new Promise((resolve, reject) => {
    request(url, {
      method: "POST",
      headers: { Accept: ACCEPT, "Content-Type": JSON_CONTENT_TYPE },
      body: JSON.stringify(body),
    })
      .then(response => {
        const data = (response.data || {}) as { chain?: unknown };
        resolve(Array.isArray(data.chain) ? data.chain : []);
      })
      .catch(error => {
        if (error.response) {
          reject(getResponseError(error.response));
        } else {
          reject({
            status: 0,
            errorString: error.toString(),
          });
        }
      });
  });
}
